import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import Image from 'next/image'
import logger from '../../services/logger'
import styled from '@emotion/styled'
import { mq } from '../../helpers'
import { getBlogs } from '../../services/strapi'
import Pagination from '../../components/blog/paginated-footer'

const DEFAULT_LIMIT = 20

export async function getServerSideProps(context) {
  // Page from query string, clamped to >= 1 so a stray ?page=0 can't request an
  // invalid page.
  const page = Math.max(1, parseInt(context.query?.page, 10) || 1)

  logger.log('Blog.getServerSideProps: fetching blog posts from Strapi', { page })
  const { ok, blogs, pagination } = await getBlogs({ page, pageSize: DEFAULT_LIMIT })

  if (ok) {
    logger.info('Blog.getServerSideProps: fetched blog posts from Strapi', { count: blogs.length })
    // Blog content changes rarely — let CloudFront cache the rendered HTML.
    // Fresh for 1h, then serve stale up to 1d while revalidating in the background.
    // Only cache a genuine success: a transient Strapi failure returns ok:false
    // and skips this header so the empty page is never cached at the edge.
    context.res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=86400')
  } else {
    logger.error('Blog.getServerSideProps: Strapi fetch failed; not caching empty render')
  }

  return {
    props: {
      blogs,
      pagination: pagination || { page, pageSize: DEFAULT_LIMIT, pageCount: 1, total: 0 },
    },
  }
}

export default function Blog({ blogs, pagination }) {
  const { page, pageCount, } = pagination || {};

  return (
    <div>
      <Head>
        <title>Glamsquad Beauty Blog</title>
        <meta name="title" content="Glamsquad Beauty Blog" />
        <meta name="description" content="Beauty tips, trends, and how-tos from the GLAMSQUAD team — hair, makeup, nails, and expert advice for looking your best at home." />
        <link rel="canonical" href="https://glamsquad.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Glamsquad Beauty Blog" />
        <meta property="og:description" content="Beauty tips, trends, and how-tos from the GLAMSQUAD team — hair, makeup, nails, and expert advice for looking your best at home." />
        <meta property="og:url" content="https://glamsquad.com/blog" />
      </Head>
      <BlogWrapper>
        <div className='header-container'>
          <h1>Glamsquad Beauty Blog</h1>
        </div>
        <div className='blogs'>
          {blogs.map(({ title, slug, featured_images: featuredImages }) => {
            if (!title) { return null; }

            return (
              <BlogCard key={slug}
                {...{
                  title,
                  featuredImages,
                  slug,
                }}
              />
            )
          })}
        </div>
        <Pagination
          page={page}
          pageCount={pageCount}
        />
      </BlogWrapper>
    </div>
  )
}

const BlogCard = ({ title, slug, featuredImages }) => {
  return (
    <BlogCardContainer>
      <div className='image'>
        {featuredImages?.length > 0 && (<Image
          src={featuredImages?.[0]?.url || ''}
          alt={title}
          fill
          sizes='(max-width: 800px) 100vw, 33vw'
          style={{ objectFit: 'cover' }}
        />)}
      </div>
      <div className='info'>
        <h3>{title}</h3>
        <Link href={`/blog/${slug}`}>Read More</Link>
      </div>
    </BlogCardContainer>
  )
}

const BlogCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f9f6f1;
  box-sizing: border-box;

  ${mq('max', '800px')} {
    flex-basis: 100%;
  }

  .image {
    position: relative;
    aspect-ratio: 16 / 9;
  }

  .info {
    padding: 20px;
    height: 100%;
    justify-content: space-between;
    display: flex;
    flex-direction: column;

    h3 {
      color: var(--Black, #000);
      font-family: Garnett;
      font-size: 20px;
      font-style: normal;
      font-weight: 750;
      line-height: 140%; /* 28px */
      letter-spacing: 0.282px;
      margin: 0;
      padding: 0;
    }

    a {
      color: var(--Orange, #07218F);
      font-family: Euclid;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 145%; /* 23.2px */
      letter-spacing: 0.16px;
      text-decoration: none;
      margin-top: 40px;
    }
  }
  
`

/*
  {
    "id": 40,
    "documentId": "xcny9yg01vecagj7e16840xy",
    "title": "Gel vs Dip Powder: Which Manicure Lasts Longer?",
    "slug": "gel-vs-dip-powder-which-manicure-lasts-longer",
    "body": "<p></p>",
    "publish_date": "2025-08-15",
    "createdAt": "2025-08-08T20:06:32.848Z",
    "updatedAt": "2025-08-08T20:06:32.848Z",
    "publishedAt": "2025-08-08T20:06:32.972Z",
    "featured_images": null
  }
]
*/


const BlogWrapper = styled.div`
  max-width: 1660px;
  .header-container {
    padding: 103px 88px;
    ${mq('max', '800px')} {
      padding: 30px 16px;
    }
    h1 {
      color: var(--Black, #000);
      font-family: Garnett;
      font-size: 82px;
      font-style: normal;
      font-weight: 750;
      line-height: 138%; /* 113.16px */
      letter-spacing: 1.64px;
      text-transform: uppercase;
      text-align: center;

      ${mq('max', '800px')} {
        font-size: 28px;
        text-align: left;
      }
    }
  }

  .blogs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 0 88px;
    gap: 17px;

    ${mq('max', '800px')} {
      display: flex;
      flex-wrap: wrap;
      padding: 0 16px;
    }
  }

  .pagination {
    justify-content: center;
    a {
      font-family: Euclid;
      text-decoration: none;
      color: black;
    }
  }
`
